/**
 * Centralized API utility for handling all network requests
 */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sandbox.rebax.in';

// Custom XMLHttpRequest wrapper to handle CORS
function corsAwareFetch(url, options) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method, url, true);

    // Set headers, but skip Content-Type for FormData
    const isFormData = options.body instanceof FormData;
    Object.keys(options.headers || {}).forEach(key => {
      // Skip Content-Type header for FormData to let browser set it automatically with boundary
      if (!(isFormData && key.toLowerCase() === 'content-type')) {
        xhr.setRequestHeader(key, options.headers[key]);
      }
    });

    // Set up event handlers
    xhr.onload = function() {
      try {
        const response = {
          status: xhr.status,
          statusText: xhr.statusText,
          ok: xhr.status >= 200 && xhr.status < 360,
          headers: {},
          text: () => Promise.resolve(xhr.responseText),
          json: () => Promise.resolve(JSON.parse(xhr.responseText))
        };

        // Manually set headers
        xhr.getAllResponseHeaders().split('\r\n').forEach(header => {
          if (header) {
            const [key, value] = header.split(': ');
            response.headers[key.toLowerCase()] = value;
          }
        });

        if (response.ok) {
          resolve(response);
        } else {
          const errorDetails = {
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: xhr.responseText
          };
          reject(errorDetails);
        }
      } catch (parseError) {
        reject(parseError);
      }
    };

    xhr.onerror = function() {
      reject(new Error('Network error'));
    };

    // Send the request
    if (options.body) {
      if (options.body instanceof FormData) {
        // For FormData, send directly without stringification
        console.log('[DEBUG] Sending FormData with entries:');
        for (let [key, value] of options.body.entries()) {
          console.log(`[DEBUG] FormData ${key}:`, value);
        }
        xhr.send(options.body);
      } else {
        xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
      }
    } else {
      xhr.send();
    }
  });
}

/**
 * Clean and normalize the endpoint for API calls
 * @param {string} endpoint - The original endpoint
 * @returns {string} Cleaned and normalized endpoint
 */
const normalizeEndpoint = (endpoint) => {
  // Remove duplicate and full URLs
  return endpoint
    .replace(new RegExp(`^https?://[^/]+/api/https?://[^/]+/api/`, 'i'), '')  // Remove repeated URLs
    .replace(/^https?:\/\/[^/]+\//, '')  // Remove full URLs
    .replace(/^\/+/, '')  // Remove leading slashes
    .replace(/^api\//, '')  // Remove leading 'api/'
    .replace(/^\/api\//, '');  // Remove '/api/' at the start
};

/**
 * Create headers for API requests with advanced configuration
 * @param {Object} options - Configuration options
 * @returns {Object} Configured headers for the request
 */
export const createApiHeaders = (options = {}) => {
  const {
    includeAuth = true,
    contentType = 'application/json',
    accept = 'application/json',
    customHeaders = {}
  } = options;

  const headers = {
    ...(contentType ? { 'Content-Type': contentType } : {}),
    ...(accept ? { 'Accept': accept } : {}),
    ...customHeaders
  };

  // Conditionally add authorization
  const authToken = localStorage.getItem('authToken');
  const userItem = localStorage.getItem('user');

  if (includeAuth) {
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // If no auth token, try to get from user object
    if (!authToken && userItem) {
      try {
        const userObj = JSON.parse(userItem);
        if (userObj.token) {
          headers['Authorization'] = `Bearer ${userObj.token}`;
        }
      } catch (parseError) {
        console.warn('Could not parse user object from localStorage:', parseError);
      }
    }

    // Add sub-user headers if applicable (matching mobile app api.js interceptor)
    const isSubUser = localStorage.getItem('isSubUser');
    if (isSubUser === 'true') {
      const actualRole = localStorage.getItem('actualRole');
      const actualUserId = localStorage.getItem('actualUserId');
      const actualUserCity = localStorage.getItem('actualUserCity');

      if (actualRole) headers['X-Employee-Type'] = actualRole;
      if (actualUserId) headers['X-Actual-User-Id'] = actualUserId;
      if (actualUserCity) headers['X-Employee-City'] = actualUserCity;
    }
  }

  return headers;
};

/**
 * Perform API fetch with advanced error handling
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Standardized API response
 */
export const apiCall = async (endpoint, options = {}) => {
  try {
    // Normalize endpoint
    const cleanEndpoint = normalizeEndpoint(endpoint);

    // ✅ Build base URL first
    let fullUrl = `${API_BASE_URL}/api/${cleanEndpoint}`;

    // ✅ FIX: Attach query params to the REAL URL
    if (options.params) {
      const queryString = new URLSearchParams(
        Object.entries(options.params).filter(([_, v]) => v !== null && v !== undefined)
      ).toString();

      if (queryString) {
        fullUrl += `?${queryString}`;
      }
    }

    console.group('🌐 API Call Diagnostics');
    console.log('Original Endpoint:', endpoint);
    console.log('Cleaned Endpoint:', cleanEndpoint);
    console.log('Final URL (with params):', fullUrl);
    console.log('Auth Token:', localStorage.getItem('authToken'));
    console.log('User Role:', localStorage.getItem('userRole'));
    console.groupEnd();

    // Merge default and provided options
    const defaultOptions = {
      method: 'GET',
      headers: createApiHeaders(options.headerOptions || {})
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options.headers || {})
      }
    };

    // Ensure body is stringified if it's an object (but NOT FormData)
    if (mergedOptions.body && typeof mergedOptions.body === 'object' && !(mergedOptions.body instanceof FormData)) {
      mergedOptions.body = JSON.stringify(mergedOptions.body);
    }

    delete mergedOptions.params;

    console.group('📤 Request Details');
    console.log('URL:', fullUrl);
    console.log('Method:', mergedOptions.method);
    console.log('Headers:', mergedOptions.headers);
    console.log('Body:', mergedOptions.body);
    console.log('Body Type:', typeof mergedOptions.body);
    console.log('Is FormData:', mergedOptions.body instanceof FormData);
    if (mergedOptions.body instanceof FormData) {
      console.log('FormData entries:');
      for (let [key, value] of mergedOptions.body.entries()) {
        console.log(`  ${key}:`, value);
      }
    }
    console.groupEnd();

    const response = await corsAwareFetch(fullUrl, mergedOptions);

    if (!response.ok) {
      const errorText = await response.text();

      // Try to parse error response as JSON first
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (parseError) {
        // If it's not JSON, treat as plain text error
        errorData = { message: errorText || response.statusText };
      }

      // If it's already a structured error response, return it directly
      if (errorData && typeof errorData === 'object' && errorData.hasOwnProperty('success')) {
        return errorData;
      }

      // Otherwise, throw as before
      throw new Error(errorText || response.statusText);
    }

    const data = await response.json();

    // Log raw API response for debugging
    if (fullUrl.includes('/api/auth/login')) {
      console.log('Raw API Response for login:', data);
      console.log('Data.data exists:', !!data.data);
      console.log('Final data will be:', data.data || data);
    }

    return {
      success: true,
      data: data.data || data,
      message: data.message || 'Request successful'
    };

  } catch (error) {
    console.error('🔥 API Error Details:', {
      message: error.message,
      name: error.name,
      stack: error.stack,
      errorObject: error
    });

    // Handle errors from corsAwareFetch (which have status, statusText, responseText)
    if (error.status && error.responseText) {
      console.error('CORS Fetch Error Details:', {
        status: error.status,
        statusText: error.statusText,
        responseText: error.responseText
      });

      // Try to parse the responseText as JSON to get the actual API error
      try {
        const errorData = JSON.parse(error.responseText);
        if (errorData && typeof errorData === 'object' && errorData.hasOwnProperty('success')) {
          return errorData; // Return the actual API error response
        }
      } catch (parseError) {
        // If responseText isn't JSON, return it as the message
        return {
          success: false,
          message: error.responseText || error.statusText || 'API call failed'
        };
      }
    }

    // If it's a fetch/network error, provide more context
    if (error instanceof TypeError) {
      console.error('Network Error Details:', {
        networkError: true,
        isOffline: !navigator.onLine,
        connectionType: navigator.connection?.type
      });
    }

    // Additional error context from response
    if (error.response) {
      console.error('Response Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseText: error.response?.responseText
      });
    }

    return {
      success: false,
      message: error.message || 'API call failed',
      error: {
        message: error.message,
        name: error.name,
        networkError: error instanceof TypeError
      }
    };
  }
};

/**
 * Perform a GET request
 * @param {string} endpoint - The API endpoint
 * @param {Object} [params={}] - Query parameters
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Object>} API response
 */
export const apiGet = (endpoint, params = {}, options = {}) => {
  return apiCall(endpoint, {
    method: 'GET',
    params,
    ...options
  });
};

/**
 * Perform a POST request
 * @param {string} endpoint - The API endpoint
 * @param {Object} body - Request body
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Object>} API response
 */
export const apiPost = (endpoint, body, options = {}) => {
  return apiCall(endpoint, {
    method: 'POST',
    body,
    ...options
  });
};

/**
 * Perform a PUT request
 * @param {string} endpoint - The API endpoint
 * @param {Object} body - Request body
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Object>} API response
 */
export const apiPut = (endpoint, body, options = {}) => {
  return apiCall(endpoint, {
    method: 'PUT',
    body,
    ...options
  });
};

/**
 * Perform a DELETE request
 * @param {string} endpoint - The API endpoint
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Object>} API response
 */
export const apiDelete = (endpoint, options = {}) => {
  return apiCall(endpoint, {
    method: 'DELETE',
    ...options
  });
};

export default {
  apiCall,
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  createApiHeaders
};
